/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  User,
  Mail,
  Shield,
  Building2,
  Globe,
  Layers,
  BadgeCheck,
  Edit2,
  Save,
  X,
  Lock,
  Copy,
  Check,
  AlertCircle,
  AtSign,
  Sun,
  Moon,
  RotateCcw,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import toastService from '../utils/toastService';
import isEmail from 'validator/lib/isEmail';
import QrLoginCard from '../components/QrLoginCard';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';
import { hasAnyRoleKey } from '../utils/scope';

// Pixo users' identity is managed by Pixo, so their profile is read-only here
const PIXO_ROLE_KEYS = ['px_account_admin', 'px_manager', 'px_technician'];

const THEME_OPTIONS = ['light', 'dark'];

const CopyButton = ({ text, label = 'value', className = '' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toastService.success(`Copied ${label} to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!text) return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Copy ${label}`}
      className={`p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all ${className}`}
    >
      {copied ? <Check size={13} className="text-success" /> : <Copy size={13} />}
    </button>
  );
};

const Profile = () => {
  const { user, updateProfile, currentTheme, chooseTheme, clearThemeChoice, hasThemeChoice } = useAuth();
  // A workspace branding theme may be something other than light/dark
  const isListedTheme = THEME_OPTIONS.includes(currentTheme);
  const isDarkTheme = currentTheme === 'dark';

  const handleThemeChange = (themeId) => {
    chooseTheme(themeId);
    toastService.success(`Theme switched to "${themeId}"`);
  };

  // Icon buttons: no-op (and no toast) when that theme is already active
  const selectTheme = (themeId) => {
    if (currentTheme !== themeId) handleThemeChange(themeId);
  };

  const handleUseWorkspaceTheme = () => {
    clearThemeChoice();
    toastService.success('Using the workspace theme');
  };
  const canEditProfile = !hasAnyRoleKey(user, PIXO_ROLE_KEYS);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
  });

  const [errors, setErrors] = useState({});

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/auth/me');
      setProfileData(data);
      setForm({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        displayName: data.displayName || '',
        email: data.email || '',
      });
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to load profile details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) { newErrors.firstName = 'First name is required'; }
    if (!form.lastName.trim()) { newErrors.lastName = 'Last name is required'; }
    if (!form.displayName.trim()) { newErrors.displayName = 'Display name is required'; }
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isEmail(form.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field, value) => {
    const newErrors = { ...errors };
    if (field === 'firstName') {
      value.trim() ? delete newErrors.firstName : (newErrors.firstName = 'First name is required');
    }
    if (field === 'lastName') {
      value.trim() ? delete newErrors.lastName : (newErrors.lastName = 'Last name is required');
    }
    if (field === 'displayName') {
      value.trim() ? delete newErrors.displayName : (newErrors.displayName = 'Display name is required');
    }
    setErrors(newErrors);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      const updated = { ...errors };
      delete updated[field];
      setErrors(updated);
    }
  };

  const handleSave = async () => {
    if (!validate()) { return; }
    setSaving(true);
    try {
      const { data } = await apiClient.put('/auth/profile', { ...form });
      updateProfile(data);
      setProfileData(data);
      toastService.success('Profile updated successfully');
      setIsEditing(false);
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profileData) {
      setForm({
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        displayName: profileData.displayName || '',
        email: profileData.email || '',
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  const getRoleBadgeColor = (role) => {
    if (role?.includes('platform')) { return 'bg-rose-500/10 text-rose-500 border-rose-500/20'; }
    if (role?.includes('account')) { return 'bg-blue-500/10 text-blue-500 border-blue-500/20'; }
    return 'bg-primary/10 text-primary border-primary/20';
  };

  const userInitials = useMemo(() => {
    const first = form.firstName?.[0] || profileData?.firstName?.[0] || '';
    const last = form.lastName?.[0] || profileData?.lastName?.[0] || '';
    const combined = (first + last).toUpperCase();
    if (combined) return combined;
    return (profileData?.displayName?.[0] || profileData?.email?.[0] || 'U').toUpperCase();
  }, [form.firstName, form.lastName, profileData?.firstName, profileData?.lastName, profileData?.displayName, profileData?.email]);

  const roleLabel = useMemo(
    () => profileData?.roleDisplayName || profileData?.role?.replace(/_/g, ' ') || 'User',
    [profileData?.roleDisplayName, profileData?.role],
  );

  const labelCls = 'text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-1 flex items-center gap-1.5 px-0.5';

  const inputCls = (field, disabled = false) =>
    `w-full px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all outline-none ` +
    (disabled
      ? 'bg-base-200/60 border border-base-content/5 text-base-content/50 cursor-not-allowed'
      : errors[field]
        ? 'bg-base-100 border border-error focus:ring-2 focus:ring-error/20 shadow-sm'
        : 'bg-base-100 border border-base-300 focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm');

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse p-1 sm:p-2 w-full">
        <div className="glass-card rounded-2xl p-5 h-28 bg-base-100/60"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="glass-card rounded-2xl p-4 h-24 bg-base-100/60"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7 xl:col-span-8">
            <div className="glass-card rounded-2xl p-5 h-72 bg-base-100/60"></div>
          </div>
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="glass-card rounded-2xl p-5 h-72 bg-base-100/60"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="glass-card rounded-2xl p-10 text-center space-y-4">
        <AlertCircle size={36} className="text-error mx-auto" />
        <h2 className="text-lg font-black text-base-content">Profile Unavailable</h2>
        <p className="text-xs text-base-content/50 max-w-sm mx-auto">
          We were unable to retrieve your profile information at this time.
        </p>
        <button
          onClick={() => fetchProfile(false)}
          className="btn btn-sm btn-primary rounded-xl font-bold"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-3.5 sm:space-y-4">

      {/* Hero Header Card matching Dashboard & Service Requests */}
      <div className="glass-card rounded-2xl p-4 sm:p-4.5 shadow-sm border border-base-content/10">
        <div className="flex items-center gap-3.5 sm:gap-4 min-w-0">
          {/* Avatar and Identity */}
          <div className="relative flex-shrink-0">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-secondary text-primary-content font-black text-lg sm:text-xl flex items-center justify-center shadow-md border-2 border-base-100 ring-2 ring-primary/20 tracking-wider">
              {userInitials}
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-success border-2 border-base-100 flex items-center justify-center" title="Active user">
              <Check size={9} className="text-success-content stroke-[3]" />
            </div>
          </div>

          <div className="space-y-1 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-black text-base-content tracking-tight truncate">
                {profileData.displayName || `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() || 'My Profile'}
              </h1>
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${getRoleBadgeColor(profileData.role)}`}>
                <Shield size={10} />
                {roleLabel}
              </span>
            </div>
            <p className="text-xs text-base-content/50 flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1 font-medium">
                <Mail size={11} className="text-base-content/40" />
                {profileData.email}
              </span>
              <span className="text-base-content/20">•</span>
              <span>Personal details, workspace context, and instant smart glasses login</span>
            </p>
          </div>
        </div>
      </div>

      {/* Main KPI Stat Cards Grid matching Dashboard layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-3.5">
        {/* Full Name / Identity */}
        <div className="glass-card rounded-2xl p-3.5 sm:p-4 border border-base-content/10 relative overflow-hidden space-y-1.5 group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50">
              Identity Name
            </span>
            <div className="w-7 h-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
              <User size={14} />
            </div>
          </div>
          <div className="text-lg font-black text-base-content truncate tracking-tight" title={`${form.firstName} ${form.lastName}`}>
            {`${form.firstName} ${form.lastName}`.trim() || '—'}
          </div>
          <p className="text-xs text-base-content/50 font-medium truncate">
            Display: {form.displayName || '—'}
          </p>
        </div>

        {/* Assigned Role */}
        <div className="glass-card rounded-2xl p-3.5 sm:p-4 border border-base-content/10 relative overflow-hidden space-y-1.5 group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50">
              Access Role
            </span>
            <div className="w-7 h-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
              <Shield size={14} />
            </div>
          </div>
          <div className="text-lg font-black text-base-content truncate tracking-tight uppercase" title={roleLabel}>
            {roleLabel}
          </div>
          <p className="text-xs text-base-content/50 font-medium truncate">
            Authorized Account Role
          </p>
        </div>

        {/* Workspace Organization */}
        <div className="glass-card rounded-2xl p-3.5 sm:p-4 border border-base-content/10 relative overflow-hidden space-y-1.5 group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50">
              Workspace Context
            </span>
            <div className="w-7 h-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
              {profileData.subAccountName ? (
                <Layers size={14} />
              ) : profileData.accountName ? (
                <Building2 size={14} />
              ) : (
                <Globe size={14} />
              )}
            </div>
          </div>
          <div className="text-lg font-black text-base-content truncate tracking-tight" title={profileData.subAccountName || profileData.accountName || 'Global Platform'}>
            {profileData.subAccountName || profileData.accountName || 'Global Platform'}
          </div>
          <p className="text-xs text-base-content/50 font-medium truncate">
            {profileData.subAccountName ? `${SUB_ACCOUNT_LABEL.singular} Workspace` : profileData.accountName ? `${ACCOUNT_LABEL.singular} Workspace` : 'Platform Level'}
          </p>
        </div>

        {/* Theme Preference */}
        <div className="glass-card rounded-2xl p-3.5 sm:p-4 border border-base-content/10 relative overflow-hidden space-y-1.5 group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-primary"></div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-widest text-base-content/50">
              Interface Theme
            </span>
            <div className="w-7 h-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
              {isDarkTheme ? <Moon size={14} /> : <Sun size={14} />}
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="text-lg font-black text-base-content truncate tracking-tight capitalize" title={currentTheme}>
              {isListedTheme ? currentTheme : `Workspace (${currentTheme})`}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => selectTheme('light')}
                aria-label="Light theme"
                title="Light"
                className={`p-1 rounded-lg transition-colors hover:bg-primary/10 ${isDarkTheme ? 'text-base-content/30 hover:text-primary' : 'text-primary'}`}
              >
                <Sun size={14} />
              </button>
              <input
                type="checkbox"
                role="switch"
                aria-checked={isDarkTheme}
                aria-label="Dark theme"
                className="toggle toggle-primary toggle-sm"
                checked={isDarkTheme}
                onChange={(e) => handleThemeChange(e.target.checked ? 'dark' : 'light')}
              />
              <button
                type="button"
                onClick={() => selectTheme('dark')}
                aria-label="Dark theme"
                title="Dark"
                className={`p-1 rounded-lg transition-colors hover:bg-primary/10 ${isDarkTheme ? 'text-primary' : 'text-base-content/30 hover:text-primary'}`}
              >
                <Moon size={14} />
              </button>
            </div>
          </div>
          {hasThemeChoice ? (
            <button
              type="button"
              onClick={handleUseWorkspaceTheme}
              className="flex items-center gap-1 text-xs font-medium text-base-content/50 hover:text-primary transition-colors"
            >
              <RotateCcw size={11} />
              Use workspace theme
            </button>
          ) : (
            <p className="text-xs text-base-content/50 font-medium truncate">
              Light or dark interface
            </p>
          )}
        </div>
      </div>

      {/* Main Content 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 sm:gap-4">

        {/* Left Column: Personal Information Card */}
        <div className="lg:col-span-7 xl:col-span-8 flex flex-col">
          <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10 space-y-3.5 flex flex-col h-full">
            
            {/* Card Section Header */}
            <div className="flex items-center justify-between pb-2.5 border-b border-base-content/10 gap-3 flex-wrap">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isEditing ? 'bg-primary text-primary-content shadow-sm' : 'bg-primary/10 text-primary'}`}>
                  {isEditing ? <Edit2 size={15} /> : <User size={15} />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm sm:text-base font-black text-base-content uppercase tracking-wider">
                      {isEditing ? 'Edit Personal Information' : 'Personal Information'}
                    </h2>
                    {isEditing && (
                      <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 animate-pulse">
                        Editing
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-base-content/50">
                    {isEditing ? 'Update your identity details and display name.' : 'Basic personal identity details and system display name.'}
                  </p>
                </div>
              </div>

              {isEditing ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={saving}
                    className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl gap-1.5 font-bold hover:bg-base-200 transition-all text-xs"
                  >
                    <X size={13} />
                    <span>Cancel</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="btn btn-sm btn-primary rounded-xl gap-1.5 font-bold shadow-sm hover:shadow transition-all text-xs"
                  >
                    {saving ? (
                      <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    ) : (
                      <Save size={13} />
                    )}
                    <span>Save Changes</span>
                  </button>
                </div>
              ) : canEditProfile && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all text-xs"
                >
                  <Edit2 size={13} />
                  <span>Edit Profile</span>
                </button>
              )}
            </div>

            {/* Card Body: View Mode vs Edit Mode */}
            {!isEditing ? (
              /* VIEW MODE: Clean structured read-only display */
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 flex-1">
                {/* First Name */}
                <div className="p-2.5 sm:p-3 rounded-xl bg-base-200/40 border border-base-content/5 space-y-0.5">
                  <div className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1.5">
                    <User size={11} className="text-primary/70" />
                    <span>First Name</span>
                  </div>
                  <div className="text-sm sm:text-base font-bold text-base-content truncate" title={form.firstName || profileData?.firstName}>
                    {form.firstName || profileData?.firstName || '—'}
                  </div>
                </div>

                {/* Last Name */}
                <div className="p-2.5 sm:p-3 rounded-xl bg-base-200/40 border border-base-content/5 space-y-0.5">
                  <div className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1.5">
                    <User size={11} className="text-primary/70" />
                    <span>Last Name</span>
                  </div>
                  <div className="text-sm sm:text-base font-bold text-base-content truncate" title={form.lastName || profileData?.lastName}>
                    {form.lastName || profileData?.lastName || '—'}
                  </div>
                </div>

                {/* Display Name */}
                <div className="p-2.5 sm:p-3 rounded-xl bg-base-200/40 border border-base-content/5 space-y-0.5 sm:col-span-2">
                  <div className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1.5">
                    <BadgeCheck size={11} className="text-primary/70" />
                    <span>Display Name</span>
                  </div>
                  <div className="text-sm sm:text-base font-bold text-base-content truncate" title={form.displayName || profileData?.displayName}>
                    {form.displayName || profileData?.displayName || '—'}
                  </div>
                  <p className="text-[11px] text-base-content/40 font-medium pt-0.5">
                    This name will appear in activity history, capture notes, and user listings.
                  </p>
                </div>

                {/* Email Address */}
                <div className="p-2.5 sm:p-3 rounded-xl bg-base-200/40 border border-base-content/5 space-y-0.5 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1.5">
                      <Mail size={11} className="text-primary/70" />
                      <span>Email Address (Primary Identity)</span>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-base-content/40 flex items-center gap-1 bg-base-content/5 px-2 py-0.5 rounded-full">
                      <Lock size={10} />
                      System Managed
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm sm:text-base font-bold text-base-content truncate font-mono" title={form.email || profileData?.email}>
                      {form.email || profileData?.email || '—'}
                    </div>
                    <CopyButton text={form.email || profileData?.email} label="Email Address" />
                  </div>
                  <p className="text-[11px] text-base-content/40 font-medium pt-0.5">
                    Your primary authentication email. Contact a platform or {ACCOUNT_LABEL.singularLower} administrator to update your address.
                  </p>
                </div>

                {/* Username — read-only, never part of the editable form */}
                {profileData?.username && (
                  <div className="p-2.5 sm:p-3 rounded-xl bg-base-200/40 border border-base-content/5 space-y-0.5 sm:col-span-2">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1.5">
                        <AtSign size={11} className="text-primary/70" />
                        <span>Username</span>
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-base-content/40 flex items-center gap-1 bg-base-content/5 px-2 py-0.5 rounded-full">
                        <Lock size={10} />
                        System Managed
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm sm:text-base font-bold text-base-content truncate font-mono" title={profileData.username}>
                        {profileData.username}
                      </div>
                      <CopyButton text={profileData.username} label="Username" />
                    </div>
                    <p className="text-[11px] text-base-content/40 font-medium pt-0.5">
                      You can sign in with your username or email. Usernames cannot be changed.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* EDIT MODE: Interactive Form Inputs */
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-3.5 flex-1">
                {/* First Name Input */}
                <div className="space-y-1">
                  <label htmlFor="profileFirstName" className={labelCls}>
                    <User size={11} className="text-primary/70" />
                    <span>First Name</span>
                    <span className="text-error font-bold">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="profileFirstName"
                      autoComplete="off"
                      type="text"
                      value={form.firstName}
                      maxLength={30}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      onBlur={(e) => handleBlur('firstName', e.target.value)}
                      placeholder="Enter first name"
                      className={inputCls('firstName')}
                      autoFocus
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-base-content/30 uppercase tracking-widest pointer-events-none">
                      {form.firstName.length}/30
                    </span>
                  </div>
                  {errors.firstName && (
                    <p className="text-error text-xs mt-1 font-semibold flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.firstName}
                    </p>
                  )}
                </div>

                {/* Last Name Input */}
                <div className="space-y-1">
                  <label htmlFor="profileLastName" className={labelCls}>
                    <User size={11} className="text-primary/70" />
                    <span>Last Name</span>
                    <span className="text-error font-bold">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="profileLastName"
                      autoComplete="off"
                      type="text"
                      value={form.lastName}
                      maxLength={30}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      onBlur={(e) => handleBlur('lastName', e.target.value)}
                      placeholder="Enter last name"
                      className={inputCls('lastName')}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-base-content/30 uppercase tracking-widest pointer-events-none">
                      {form.lastName.length}/30
                    </span>
                  </div>
                  {errors.lastName && (
                    <p className="text-error text-xs mt-1 font-semibold flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.lastName}
                    </p>
                  )}
                </div>

                {/* Display Name Input */}
                <div className="space-y-1 sm:col-span-2">
                  <label htmlFor="profileDisplayName" className={labelCls}>
                    <BadgeCheck size={11} className="text-primary/70" />
                    <span>Display Name</span>
                    <span className="text-error font-bold">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="profileDisplayName"
                      autoComplete="off"
                      type="text"
                      value={form.displayName}
                      maxLength={30}
                      onChange={(e) => handleChange('displayName', e.target.value)}
                      onBlur={(e) => handleBlur('displayName', e.target.value)}
                      placeholder="Enter display name"
                      className={inputCls('displayName')}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-bold text-base-content/30 uppercase tracking-widest pointer-events-none">
                      {form.displayName.length}/30
                    </span>
                  </div>
                  {errors.displayName && (
                    <p className="text-error text-xs mt-1 font-semibold flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.displayName}
                    </p>
                  )}
                  <p className="text-[11px] text-base-content/40 font-medium pt-0.5">
                    This name will appear in activity history, capture notes, and user listings.
                  </p>
                </div>

                {/* Email Input — System Managed */}
                <div className="space-y-1 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="profileEmail" className={labelCls}>
                      <Mail size={11} className="text-primary/70" />
                      <span>Email Address (Primary Identity)</span>
                    </label>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-base-content/40 flex items-center gap-1">
                      <Lock size={10} />
                      System Managed
                    </span>
                  </div>
                  <div className="relative">
                    <input
                      id="profileEmail"
                      type="email"
                      value={form.email}
                      readOnly
                      disabled
                      className={inputCls('email', true)}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CopyButton text={form.email} label="Email Address" />
                    </div>
                  </div>
                  <p className="text-[11px] text-base-content/40 font-medium pt-0.5">
                    Your primary authentication email. Contact a platform or {ACCOUNT_LABEL.singularLower} administrator to update your address.
                  </p>
                </div>

                {/* Username — read-only, never part of the editable form */}
                {profileData?.username && (
                  <div className="space-y-1 sm:col-span-2">
                    <div className="flex items-center justify-between">
                      <label htmlFor="profileUsername" className={labelCls}>
                        <AtSign size={11} className="text-primary/70" />
                        <span>Username</span>
                      </label>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-base-content/40 flex items-center gap-1">
                        <Lock size={10} />
                        System Managed
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        id="profileUsername"
                        type="text"
                        value={profileData.username}
                        readOnly
                        disabled
                        className={inputCls('username', true)}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <CopyButton text={profileData.username} label="Username" />
                      </div>
                    </div>
                    <p className="text-[11px] text-base-content/40 font-medium pt-0.5">
                      Usernames cannot be changed.
                    </p>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Right Column: QR Login */}
        <div className="lg:col-span-5 xl:col-span-4 flex flex-col">
          {/* QR Instant Login Card */}
          <QrLoginCard />
        </div>

      </div>

    </div>
  );
};

export default Profile;